import { SiteUrls } from '@tableau/shared-urls';
import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import * as React from 'react';
import * as _ from 'underscore';

import { RefreshContentScope } from '../../app/ActionPlaceScopes';
import { CommonStyles } from '../../common/react/CommonStyles';
import { AngularActionsCell } from '../../common/react/components/dataGrid/ActionsCell';
import { AngularToReactDataGridBridge } from '../../common/react/components/dataGrid/AngularToReactDataGridBridge';
import { DataGridCells } from '../../common/react/components/dataGrid/DataGridCells';
import { SortableHeader } from '../../common/react/components/dataGrid/SortableHeader';
import { AngularContextFactory } from '../factories/AngularContextFactory';
import { ActionButtonsHelper, SubplaceWithActionButtonsScope } from '../helpers/ActionButtonsHelper';
import { ContentFiltersKeys } from '../models/ContentFilters';
import { DatasourceContentType, FlowContentType, RevisionContentType, WorkbookContentType } from '../models/ContentTypes';
import { Module } from '../Module';
import { DatasourceRevisions } from '../resources/DatasourceRevisions';
import { FlowRevisions } from '../resources/FlowRevisions';
import { WorkbookRevisions } from '../resources/WorkbookRevisions';
import { IContentRevision } from '../ServerApi/types/IContentRevision';
import { IActionButton } from '../services/ActionPanelService';
import { IQueryUpdater, QueryService } from '../services/QueryService';
import { RevisionActions } from '../services/RevisionActions';
import { RevisionHistoryAction } from '../services/RevisionHistoryAction';
import { ServerService } from '../services/ServerService';
import { SubplaceWithDataGridScope } from './ContentSubplaceCtrl';

export class RevisionHistoryDialogCtrl {

  public static $inject = [
    '$scope',
    '$translate',
    'AngularToReactDataGridBridge',
    'QueryService',
    'AngularContextFactory',
    'DatasourceRevisions',
    'FlowRevisions',
    'RevisionActions',
    'ServerService',
    'WorkbookRevisions',
  ];

  private contentType: string;

  constructor(
    private $scope: RevisionHistoryDialogCtrl.Scope,
    private $translate: ng.translate.ITranslateService,
    private AngularToReactDataGridBridge: AngularToReactDataGridBridge,
    private queryService: QueryService,
    private AngularContextFactory: AngularContextFactory,
    private DatasourceRevisions: DatasourceRevisions,
    private FlowRevisions: FlowRevisions,
    private revisionActions: RevisionActions,
    private ServerService: ServerService,
    private WorkbookRevisions: WorkbookRevisions,
  ) {

    this.configureForReactDataGrid();

    this.contentType = this.$scope.contentType.name;

    const queryUpdater = this.createQueryUpdater();

    $scope.refreshContent = () => {
      queryUpdater.refresh();
    };
    $scope.refreshContent();

    switch (this.contentType) {
      case WorkbookContentType.name:
        $scope.menuActions = this.revisionActions.getWorkbookRevisionActionButtons();
        break;
      case DatasourceContentType.name:
        $scope.menuActions = this.revisionActions.getDatasourceRevisionActionButtons();
        break;
      case FlowContentType.name:
        $scope.menuActions = this.revisionActions.getFlowRevisionActionButtons();
        break;
      default:
        throw new Error(this.$translate.instant('revisionHistoryUnsupportedContentType'));
    }
    $scope.availableAction = (action: IActionButton<IContentRevision, {}>) => action.isAvailable($scope.selectedItems);
    ActionButtonsHelper.setupScopeForAngularActionsMenu($scope, $scope.menuActions);
  }

  private configureForReactDataGrid() {
    const selectRow = (item: IContentRevision, columnKey?: string) => {
      this.$scope.$apply(() => {
        if (!item.deleted && !item.current) {
          const onlySelectedRow = this.$scope.selectedItems.size() === 1 && this.$scope.selectedItems.contains(item);
          const isActionMenu = columnKey === 'actions';
          if (onlySelectedRow && !isActionMenu) {
            this.$scope.selectedItems.toggle(item);
          } else {
            this.$scope.selectedItems.clear();
            this.$scope.selectedItems.add(item);
          }

          // TFS 769654 - Use the new selection model when we upgrade to Data Grid 3.0
          // this.$scope.dataGridProps.selectionModel = new VizPortalGridSelection(this.$scope.selectedItems);

          const keys = this.$scope.selectedItems.keys();
          this.$scope.dataGridProps.selectionData = <_.Dictionary<string>>_.object(keys, keys);
        }
      });
    };

    // New cell click handler for Data Grid 2/3
    // const cellClickHandler = (item: IContentRevision, cell: DataGrid.Cell) => selectRow(item, cell.colKey);

    const cellClickHandler = (item: IContentRevision, columnKey: string) => selectRow(item, columnKey);
    const rowClickHandler = (item: any) => selectRow(item);

    this.$scope.dataGridProps = {
      cols: this.dataGridColumns(this.$scope),
      hackyUpdate: 0,
      onCellClick: cellClickHandler,

      // TODO: When this prop is removed (when we upgrade the data grid), can we still select a row
      // by clicking in the blank area to the right of the last cell?
      onRowClick: rowClickHandler,
    };
  }

  private createQueryUpdater(): IQueryUpdater {
    let resource;
    let filterKey;

    switch (this.contentType) {
      case WorkbookContentType.name:
        resource = this.WorkbookRevisions;
        filterKey = ContentFiltersKeys.workbook;
        break;
      case DatasourceContentType.name:
        resource = this.DatasourceRevisions;
        filterKey = ContentFiltersKeys.datasource;
        break;
      case FlowContentType.name:
        resource = this.FlowRevisions;
        filterKey = ContentFiltersKeys.flow;
        break;
      default:
        throw new Error(this.$translate.instant('revisionHistoryUnsupportedContentType'));
    }

    const filter: any = {};
    filter[filterKey] = this.$scope.contentItemId;

    return this.queryService.createQueryUpdater<IContentRevision>(
      this.$scope,
      RevisionContentType,
      resource,
      (resourceQuery) => {

        this.AngularToReactDataGridBridge.updateReactDataGridRowStore({
          scope: this.$scope,
          sliceable: resourceQuery,
          getRowStyle: this.getRowStyle
        });

        this.$scope.sliceable = resourceQuery;
      },
      filter
    );
  }

  private dataGridColumns($scope: RevisionHistoryDialogCtrl.Scope): DataGrid.Column[] {

    const columns: DataGrid.Column[] = [];

    columns.push({
      name: 'version-num-col',
      dataKey: 'versionNumber',
      headerText: this.$translate.instant('revisionHistoryRevisionNumberColumn'),
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: this.revisionNameCellFormatter(),
      resizable: false,
      growToFit: true,
      sortable: false
    });

    columns.push({
      name: 'publisher-col',
      dataKey: 'publisher',
      headerText: this.$translate.instant('revisionHistoryPublisherColumn'),
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: this.publisherNameCellFormatter(),
      resizable: false,
      growToFit: true,
      sortable: false
    });

    columns.push({
      name: 'publish-time-col',
      dataKey: 'publishTime',
      headerText: this.$translate.instant('revisionHistoryPublishDateColumn'),
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.DateTimeCell('publishTime'),
      resizable: false,
      growToFit: true,
      sortable: false
    });

    columns.push({
      name: 'actions-col',
      dataKey: 'actions',
      headerText: '',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: this.revisionActionsCellFormatter($scope),
      cellStyle: RevisionHistoryDialogCtrl.ActionsCellStyle, // TODO: TFS 769624 - Use StyledCellFormatter instead of cellStyle
      resizable: false,
      sortable: false
    });

    return columns;
  }

  private static ActionsCellStyle: React.CSSProperties = {
    textAlign: 'right',
    boxSizing: 'content-box',
    width: CommonStyles.Sizes.CellHeight,
  };

  private revisionNameCellFormatter(): React.StatelessComponent<BodyCell.Props> {
    const $translate = this.$translate;
    return (props: BodyCell.Props) => {
      const revision = props.row.data as IContentRevision;

      const revisionName = $translate.instant('revisionHistoryRevisionNumberLabel', { REVISION_NUMBER: props.value });
      let revisionNameElement: JSX.Element = React.createElement('span', {}, revisionName);
      if (this.contentType === WorkbookContentType.name && !revision.deleted && !revision.current) {
        // the name element links to Preview
        const path = new SiteUrls({
          siteUrlName: this.ServerService.sessionSiteUrlName(),
          queryString: window.location.search
        }).workbookRevisionPreviewUrl(revision.revisionPath);
        revisionNameElement = React.createElement('a', { href: path, target: '_blank' }, revisionName);
      }

      let revisionStateElement: React.ReactElement<React.HTMLAttributes<HTMLElement>> = null;
      if (revision.deleted) {
        revisionStateElement = React.createElement('span', {}, ' (' + $translate.instant('revisionDeletedLabel') + ')');
      } else if (revision.current) {
        revisionStateElement = React.createElement('span', { className: 'tb-disabled' }, ' (' + $translate.instant('revisionCurrentLabel') + ')');
      }

      return revisionStateElement
        ? React.createElement('div', {},
          revisionNameElement,
          revisionStateElement
        )
        : revisionNameElement;
    };
  }

  private publisherNameCellFormatter(): React.StatelessComponent<BodyCell.Props> {
    const $translate = this.$translate;
    return (props: BodyCell.Props) => {
      if (props.row.data.publisher) {
        let userNameElement: JSX.Element = React.createElement('span', {}, props.row.data.publisher);
        return userNameElement;
      } else {
        let hiddenUserNameElement: JSX.Element = React.createElement('span', {className: 'tb-hidden-user' }, $translate.instant('hiddenUser'));
        return hiddenUserNameElement;
      }
    };
  }

  private revisionActionsCellFormatter($scope: RevisionHistoryDialogCtrl.Scope): React.StatelessComponent<BodyCell.Props> {
    const actionsCell = AngularActionsCell(this.AngularContextFactory.create($scope))({});
    return (props: BodyCell.Props) => {
      const revision = <IContentRevision>props.row.data;

      return React.createElement('div',
        {
          style: {
            display: revision.deleted || revision.current
              ? 'none'
              : 'initial'
          }
        },
        actionsCell);
    };
  }

  // TODO: TFS 769624 - Replace rowStyle with extra styles in the cell formatters
  private getRowStyle(revision: IContentRevision): React.CSSProperties {
    let rowStyle: React.CSSProperties = null;
    if (revision.deleted) {
      rowStyle = {
        backgroundColor: CommonStyles.Colors.White,
        color: CommonStyles.Colors.DisabledTextColor
      };
    } else if (revision.current) {
      rowStyle = {
        backgroundColor: CommonStyles.Colors.White
      };
    }
    return rowStyle;
  }

}

export module RevisionHistoryDialogCtrl {

  export interface Scope
  extends RevisionHistoryAction.Scope,
          RefreshContentScope,
          SubplaceWithActionButtonsScope<IContentRevision, {}>,
          SubplaceWithDataGridScope<IContentRevision> {
  }

}

Module.instance().controller('RevisionHistoryDialogCtrl', RevisionHistoryDialogCtrl);
